import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const api = axios.create({
  baseURL: '/api',
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401 && !useAuthStore.getState().isDemo) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authApi = {
  googleLogin: (credential: string) =>
    api.post('/auth/google', { credential }).then((r) => r.data),
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
};

// Dashboard
export const dashboardApi = {
  get: () => api.get('/dashboard').then((r) => r.data),
};

// Projects
export const projectsApi = {
  list: (department?: string) =>
    api.get('/projects', { params: department ? { department } : {} }).then((r) => r.data),
  /** ERP-55: proyectos con status='ready_to_pack', ordenados por priority DESC y deadline ASC */
  readyToPack: (): Promise<import('../types').ReadyToPackResponse> =>
    api.get('/projects/ready-to-pack').then((r) => r.data),
  get: (id: number) => api.get(`/projects/${id}`).then((r) => r.data),
  create: (data: import('../types').CreateProjectPayload) => api.post('/projects', data).then((r) => r.data),
  update: (id: number, data: any) => api.patch(`/projects/${id}`, data).then((r) => r.data),
  updateStep: (id: number, stepId: number, data: any) =>
    api.patch(`/projects/${id}/step/${stepId}`, data).then((r) => r.data),
  savePackaging: (projectId: number, stepId: number, packaging: import('../types').PackagingRecord) =>
    api.post(`/steps/${stepId}/packaging`, {
      box_type: packaging.box_type,
      bundle_count: packaging.bundle_count,
      estimated_weight: packaging.estimated_weight,
      notes: packaging.notes ?? null,
      tracking_number: packaging.tracking_number ?? null,
      shipping_carrier: packaging.shipping_carrier ?? null,
    }).then((r) => r.data),
  shipStep: (stepId: number, trackingNumber?: string, carrier?: string) =>
    api.post(`/steps/${stepId}/packaging/ship`, {
      tracking_number: trackingNumber ?? null,
      shipping_carrier: carrier ?? null,
    }).then((r) => r.data),
  getShippingLabel: (stepId: number) =>
    api.get(`/steps/${stepId}/packaging/label`).then((r) => r.data),
};

// Clients
export const clientsApi = {
  list: (q?: string) => api.get('/clients', { params: q ? { q } : {} }).then((r) => r.data),
  get: (id: number) => api.get(`/clients/${id}`).then((r) => r.data),
  history: (id: number) => api.get(`/clients/${id}/history`).then((r) => r.data),
  create: (data: any) => api.post('/clients', data).then((r) => r.data),
  update: (id: number, data: any) => api.patch(`/clients/${id}`, data).then((r) => r.data),
};

// Templates
export const templatesApi = {
  list: (includeArchived?: boolean): Promise<import('../types').ProjectTemplate[]> =>
    api.get('/templates', { params: includeArchived ? { includeArchived: 'true' } : {} }).then((r) => {
      const payload = r.data;
      // Backend returns { data: ProjectTemplate[], total: number }
      if (payload && Array.isArray(payload.data)) return payload.data;
      return Array.isArray(payload) ? payload : [];
    }),
  // Backend returns { data: ProjectTemplate } for single-item endpoints
  get: (id: number): Promise<import('../types').ProjectTemplate> =>
    api.get(`/templates/${id}`).then((r) => r.data?.data ?? r.data),
  create: (data: any): Promise<import('../types').ProjectTemplate> =>
    api.post('/templates', data).then((r) => r.data?.data ?? r.data),
  update: (id: number, data: any): Promise<import('../types').ProjectTemplate> =>
    api.patch(`/templates/${id}`, data).then((r) => r.data?.data ?? r.data),
  delete: (id: number) => api.delete(`/templates/${id}`),
  clone: (id: number): Promise<import('../types').ProjectTemplate> =>
    api.post(`/templates/${id}/clone`).then((r) => r.data?.data ?? r.data),
  archive: (id: number): Promise<import('../types').ProjectTemplate> =>
    api.patch(`/templates/${id}`, { isArchived: true }).then((r) => r.data?.data ?? r.data),
  unarchive: (id: number): Promise<import('../types').ProjectTemplate> =>
    api.patch(`/templates/${id}`, { isArchived: false }).then((r) => r.data?.data ?? r.data),
  getVersions: (id: number) => api.get(`/templates/${id}/versions`).then((r) => r.data),
  getActiveProjectsCount: (id: number) =>
    api.get(`/templates/${id}/active-projects-count`).then((r) => r.data),
};

// Stock
export const stockApi = {
  list: (lowOnly?: boolean) =>
    api.get('/stock', { params: lowOnly ? { low: 1 } : {} }).then((r) => r.data),
  create: (data: any) => api.post('/stock', data).then((r) => r.data),
  update: (id: number, data: any) => api.patch(`/stock/${id}`, data).then((r) => r.data),
  addEntry: (id: number, payload: import('../types').StockEntry) =>
    api.post(`/stock/${id}/add`, payload).then((r) => r.data),
};

// Invoices
//
// NOTA ERP-52: El backend usa SELECT FOR UPDATE para evitar race conditions al
// generar invoice_number (FAC-YYYY-NNN). En caso de violación de UNIQUE constraint
// (error 500) durante alta concurrencia, el frontend realiza 1 reintento automático
// con un delay de 500ms antes de mostrar el error al usuario.
const isInvoiceNumberConflict = (error: any): boolean => {
  const status = error?.response?.status;
  const message: string =
    error?.response?.data?.detail ??
    error?.response?.data?.message ??
    error?.message ??
    '';
  return (
    status === 500 &&
    (message.toLowerCase().includes('unique') ||
      message.toLowerCase().includes('duplicate') ||
      message.toLowerCase().includes('invoice_number'))
  );
};

export const invoicesApi = {
  list: (params?: { status?: string; q?: string }) =>
    api.get('/invoices', { params }).then((r) => {
      const payload = r.data;
      // Backend returns { data: Invoice[], total: number } (API Platform)
      if (payload && Array.isArray(payload.data)) {
        return { data: payload.data, total: payload.total as number };
      }
      // Fallback: plain array (defensive)
      return { data: Array.isArray(payload) ? payload : [], total: 0 };
    }),
  get: (id: number) => api.get(`/invoices/${id}`).then((r) => r.data),
  create: async (data: any) => {
    try {
      return await api.post('/invoices', data).then((r) => r.data);
    } catch (error) {
      // Reintento automático (1 vez, 500ms delay) ante posible race condition
      // en la generación del número de factura FAC-YYYY-NNN (ERP-52)
      if (isInvoiceNumberConflict(error)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return api.post('/invoices', data).then((r) => r.data);
      }
      throw error;
    }
  },
  update: (id: number, data: any) => api.patch(`/invoices/${id}`, data).then((r) => r.data),
  changeStatus: (id: number, status: string) =>
    api.patch(`/invoices/${id}/status`, { status }).then((r) => r.data),
  delete: (id: number) => api.delete(`/invoices/${id}`).then((r) => r.data),
  downloadPdf: async (id: number, invoiceNumber: string): Promise<void> => {
    const token = useAuthStore.getState().token;
    const response = await fetch(`/api/invoices/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`Error ${response.status}: No se pudo generar el PDF`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factura-${invoiceNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  sendEmail: (id: number, email: string) =>
    api.post(`/invoices/${id}/send-email`, { email }).then((r) => r.data),
};

// Machines
export const machinesApi = {
  list: (department?: string): Promise<import('../types').Machine[]> =>
    api.get('/machines', { params: department ? { department } : {} }).then((r) => {
      const items: Array<{
        id: number; name: string; type: string; department: string;
        status: 'available' | 'in_use' | 'maintenance';
        description: string | null; createdAt: string;
      }> = Array.isArray(r.data?.data) ? r.data.data : [];
      return items.map((m) => ({
        id: m.id,
        name: m.name,
        type: m.type,
        department: m.department,
        status: m.status,
        isAvailable: m.status === 'available',
        description: m.description ?? undefined,
        createdAt: m.createdAt,
      }));
    }),
  assignToStep: (stepId: number, machineId: number) =>
    api.patch(`/project_steps/${stepId}`, { machine: `/api/machines/${machineId}` }).then((r) => r.data),
};

// Material Consumption
export const materialConsumptionApi = {
  list: (params?: { materialId?: number; projectId?: number }) =>
    api.get('/material-consumption', { params }).then((r) => r.data),
};

// Users
export const usersApi = {
  list: () => api.get('/admin/users').then((r) => r.data),
  toggleActive: (id: number, active: boolean) =>
    api.patch(`/admin/users/${id}/active`, { active }).then((r) => r.data),
  updateRoles: (id: number, roles: string[]) =>
    api.patch(`/admin/users/${id}/roles`, { roles }).then((r) => r.data),
  createUser: (payload: { email: string; name: string; password: string; roles?: string[] }) =>
    api.post('/admin/users', payload).then((r) => r.data),
};
